/**
 * Shrink-to-fit for text boxes. Runs identically in the editor and in the render worker
 * because both use this module through the <Scene> component.
 */

export function overflows(box: HTMLElement, text: HTMLElement): boolean {
  const cs = getComputedStyle(box);
  const availH = box.clientHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom);
  // Layout height of the (visible) lines, not ink: glyphs of tight line heights overhang.
  // Lines hidden by a clamp are caught by the check below.
  const height = text.offsetHeight;
  if (height > availH + 0.5) return true;
  if (text.scrollWidth > text.clientWidth + 0.5) return true;
  // Line-clamped text hides extra lines instead of growing. Glyphs of tight line heights poke a
  // few pixels out of the line box, so only a hidden line (≥ half a line) counts as overflow.
  if (text.dataset.clamped === 'true') {
    const line = parseFloat(getComputedStyle(text).lineHeight) || 0;
    if (text.scrollHeight > text.clientHeight + Math.max(0.5, line / 2)) return true;
  }
  return false;
}

/**
 * Binary-search the largest font size between `min` and `max` that fits.
 * Returns the chosen size (also applied to `text.style.fontSize`).
 */
export function fitText(box: HTMLElement, text: HTMLElement, max: number, min: number): number {
  const lower = Math.max(1, Math.min(min, max));
  text.style.fontSize = `${max}px`;
  if (!overflows(box, text)) return max;
  let lo = lower;
  let hi = max;
  for (let i = 0; i < 14 && hi - lo > 0.25; i++) {
    const mid = (lo + hi) / 2;
    text.style.fontSize = `${mid}px`;
    if (overflows(box, text)) hi = mid;
    else lo = mid;
  }
  const size = Math.floor(lo * 4) / 4;
  text.style.fontSize = `${size}px`;
  return size;
}

export const REFIT_EVENT = 'bm:refit';

/** Ask every mounted text layer to recompute its size (e.g. after fonts finished loading). */
export function requestRefit(): void {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(REFIT_EVENT));
}
